// src/pages/Familiares/GestionFamiliares.jsx
import React, { useState, useEffect } from "react";
import {
  Button, Form, Input, DatePicker,
  Select, Space, Modal,
  Divider, Popconfirm, Radio
} from "antd";
import {
  UserOutlined, TeamOutlined, ExclamationCircleOutlined,
  PhoneOutlined, MailOutlined,
  MedicineBoxOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/es";
import Familiares from "../../../../service/Familiares";
import { useNotify, useWithNotify } from "../../../../providers/NotificationProvider";
import useAuthContext from "../../../../hooks/useAuthContext";
import { FiPlus, FiInfo, FiCreditCard, FiPhone, FiMail, FiEdit2, FiTrash2 } from "react-icons/fi";
import EncabezadoPagina from "../../../Ui/Cliente/EncabezadoPagina";
import EstadoVacio, { claseBotonPrimario } from "../../../Ui/Cliente/EstadoVacio";

dayjs.locale("es");

const { Option } = Select;

// Una carga "declara condición" cuando el apoderado escribió algo distinto de "Ninguna"
const tieneCondicionSalud = (valor) => {
  const v = (valor || '').trim();
  return v !== '' && v.toLowerCase() !== 'ninguna';
};

export default function GestionFamiliares() {
  const { user } = useAuthContext();

  const [familiares,       setFamiliares]       = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [modalVisible,     setModalVisible]     = useState(false);
  const [modalMode,        setModalMode]        = useState('create');
  const [selectedFamiliar, setSelectedFamiliar] = useState(null);
  const [form]             = Form.useForm();

  const notify     = useNotify();
  const withNotify = useWithNotify();

  useEffect(() => { loadFamiliares(); }, []);

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return null;
    return dayjs().diff(dayjs(fechaNacimiento), 'year');
  };

  const validarEdadPermitida = async (rule, value) => {
    if (!value || typeof value !== 'object' || !value.$d) return Promise.resolve();
    try {
      const hoy     = dayjs();
      const fechaNac = dayjs(value);
      if (!fechaNac.isValid())   return Promise.reject(new Error('Fecha inválida'));
      if (fechaNac.isAfter(hoy)) return Promise.reject(new Error('La fecha no puede ser en el futuro'));
      const edad = hoy.diff(fechaNac, 'year');
      if (edad >= 18 && edad < 60) return Promise.reject(new Error('Solo puedes agregar menores de 18 años o adultos mayores de 60 años'));
      return Promise.resolve();
    } catch { return Promise.resolve(); }
  };

  const loadFamiliares = async () => {
    try {
      setLoading(true);
      setFamiliares((await Familiares.List()) || []);
    } catch (error) {
      console.error('Error cargando familiares:', error);
      notify.error(error.error || "Error al cargar familiares");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setSelectedFamiliar(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalMode('edit');
    setSelectedFamiliar(record);
    setTimeout(() => {
      form.setFieldsValue({
        nombre:          record.nombre,
        rut:             record.rut,
        fechaNacimiento: record.fechaNacimiento ? dayjs(record.fechaNacimiento) : null,
        genero:          record.genero,
        telefono:        record.telefono || '',
        email:           record.email    || '',
        tieneCondicion:    tieneCondicionSalud(record.enfermedadesBases) ? 'si' : 'no',
        enfermedadesBases: tieneCondicionSalud(record.enfermedadesBases) ? record.enfermedadesBases : '',
      });
    }, 100);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    form.resetFields();
    setSelectedFamiliar(null);
  };

  const handleSubmit = async (values) => {
    try {
      const data = {
        nombre:          values.nombre,
        rut:             values.rut,
        fechaNacimiento: values.fechaNacimiento.toISOString(),
        genero:          values.genero,
        telefono:        values.telefono || user?.phoneNumber || '',
        email:           values.email    || user?.email       || '',
        enfermedadesBases: values.tieneCondicion === 'si'
          ? (values.enfermedadesBases || '').trim()
          : 'Ninguna',
      };
      if (modalMode === 'create') {
        await withNotify(Familiares.Create(data), { loading: "Creando...", success: "Familiar creado", error: (e) => e.error || "Error al crear" });
      } else {
        await withNotify(Familiares.Update(selectedFamiliar._id, data), { loading: "Actualizando...", success: "Familiar actualizado", error: (e) => e.error || "Error al actualizar" });
      }
      handleCloseModal();
      loadFamiliares();
    } catch (error) {
      console.error('Error guardando familiar:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await withNotify(Familiares.Delete(id), { loading: "Eliminando...", success: "Familiar eliminado", error: (e) => e.error || "Error al eliminar" });
      loadFamiliares();
    } catch (error) {
      console.error('Error eliminando familiar:', error);
    }
  };

  const getCategoriaEdad = (fechaNacimiento) => {
    const edad = calcularEdad(fechaNacimiento);
    if (edad < 18)  return { texto: 'Menor',       color: 'blue'   };
    if (edad >= 60) return { texto: 'Adulto Mayor', color: 'purple' };
    return              { texto: 'Adulto',          color: 'blue'  };
  };

  // ── Iniciales del nombre ──────────────────────────────────────────────────
  const getInitials = (nombre) => {
    if (!nombre) return '?';
    return nombre.split(' ').filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase();
  };

  const AVATAR_BG = { Menor: 'bg-primary', 'Adulto Mayor': 'bg-[#00266b]', Adulto: 'bg-primary' };

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <EncabezadoPagina
          icon={<TeamOutlined />}
          titulo="Mis"
          destacado="cargas"
          subtitulo="Familiares que puedes inscribir en talleres"
        >
          {familiares.length > 0 && (
            <button type="button" onClick={handleCreate} className={claseBotonPrimario}>
              <FiPlus size={16} /> Agregar familiar
            </button>
          )}
        </EncabezadoPagina>

        {/* ── Info: a quién se puede agregar ─────────────────────────────── */}
        <div className="flex items-start gap-4 p-5 mb-8 rounded-2xl bg-[#fffbea] border border-[#fde68a]">
          <span className="w-10 h-10 flex-shrink-0 rounded-xl bg-secondary text-[#00266b] flex items-center justify-center">
            <FiInfo size={18} />
          </span>
          <div>
            <p className="font-bold text-gray-900 mb-1">¿A quién puedo agregar?</p>
            <p className="text-sm text-[#5c4300] leading-relaxed m-0">
              A <strong>menores de 18 años</strong> y <strong>adultos mayores de 60</strong>.
              Quienes tienen entre 18 y 59 años deben crear su propia cuenta.
            </p>
          </div>
        </div>

        {/* ── Loading ────────────────────────────────────────────────────── */}
        {loading && familiares.length === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" aria-busy="true" aria-label="Cargando familiares">
            {[0, 1, 2].map(i => (
              <div key={i} className="rounded-3xl bg-white p-6 animate-pulse">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-14 h-14 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded-full w-3/4" />
                    <div className="h-3 bg-gray-100 rounded-full w-1/2" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-3 bg-gray-100 rounded-full" />
                  <div className="h-3 bg-gray-100 rounded-full w-2/3" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Vacío ──────────────────────────────────────────────────────── */}
        {!loading && familiares.length === 0 && (
          <EstadoVacio
            icon={<TeamOutlined />}
            titulo="Aún no agregas familiares"
            texto="Agrega a tus hijos o a un adulto mayor a tu cargo y podrás inscribirlos en talleres desde tu cuenta, sin que tengan que registrarse."
            acciones={
              <button type="button" onClick={handleCreate} className={claseBotonPrimario}>
                <FiPlus size={16} /> Agregar mi primer familiar
              </button>
            }
          />
        )}

        {/* ── Grid de tarjetas ───────────────────────────────────────────── */}
        {familiares.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {familiares.map((f) => {
              const edad      = calcularEdad(f.fechaNacimiento);
              const categoria = getCategoriaEdad(f.fechaNacimiento);
              const telefono  = f.telefono || user?.phoneNumber;
              const email     = f.email    || user?.email;

              return (
                <article key={f._id}
                  className="relative flex flex-col rounded-3xl bg-white border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,.05),0_8px_24px_-12px_rgba(0,0,0,.08)] overflow-hidden">
                  <div aria-hidden className="h-1.5 bg-secondary" />

                  <div className="p-5 flex-1 flex flex-col">
                    {/* Avatar + nombre */}
                    <div className="flex items-center gap-3 mb-5">
                      <div className="relative flex-shrink-0">
                        <div className={`w-14 h-14 rounded-full ${AVATAR_BG[categoria.texto] || 'bg-primary'} text-white text-lg font-extrabold flex items-center justify-center`}>
                          {getInitials(f.nombre)}
                        </div>
                        <span aria-hidden className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-secondary ring-[3px] ring-white" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-extrabold text-gray-900 truncate m-0" title={f.nombre}>{f.nombre}</h3>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          <span className="px-2.5 py-0.5 rounded-full bg-secondary/30 text-[#5c4300] text-xs font-bold">
                            {categoria.texto} · {edad} año{edad !== 1 ? 's' : ''}
                          </span>
                          {f.genero && (
                            <span className="px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold capitalize">{f.genero}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Datos */}
                    <dl className="space-y-2.5 text-sm m-0 mb-5">
                      <div className="flex items-center gap-2.5">
                        <dt className="w-7 h-7 flex-shrink-0 rounded-lg bg-blue-50 text-primary flex items-center justify-center">
                          <FiCreditCard size={14} aria-label="RUT" />
                        </dt>
                        <dd className="m-0 font-mono font-semibold text-gray-800">{f.rut}</dd>
                      </div>
                      {telefono && (
                        <div className="flex items-center gap-2.5">
                          <dt className="w-7 h-7 flex-shrink-0 rounded-lg bg-blue-50 text-primary flex items-center justify-center">
                            <FiPhone size={14} aria-label="Teléfono" />
                          </dt>
                          <dd className="m-0 text-gray-700 flex items-center gap-1.5 min-w-0">
                            <span className="truncate">{telefono}</span>
                            {!f.telefono && <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-400">tuyo</span>}
                          </dd>
                        </div>
                      )}
                      {email && (
                        <div className="flex items-center gap-2.5">
                          <dt className="w-7 h-7 flex-shrink-0 rounded-lg bg-blue-50 text-primary flex items-center justify-center">
                            <FiMail size={14} aria-label="Email" />
                          </dt>
                          <dd className="m-0 text-gray-700 flex items-center gap-1.5 min-w-0">
                            <span className="truncate" title={email}>{email}</span>
                            {!f.email && <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-400">tuyo</span>}
                          </dd>
                        </div>
                      )}
                    </dl>

                    {/* Salud */}
                    {tieneCondicionSalud(f.enfermedadesBases) ? (
                      <div className="flex items-start gap-2 p-3 rounded-2xl bg-[#fff7ed] border border-[#fed7aa] text-sm text-[#9a3412] mb-5">
                        <MedicineBoxOutlined className="mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2" title={f.enfermedadesBases}>{f.enfermedadesBases}</span>
                      </div>
                    ) : (
                      <p className="flex items-center gap-2 text-sm text-gray-500 mb-5">
                        <MedicineBoxOutlined /> Sin condiciones de salud declaradas
                      </p>
                    )}

                    {/* Acciones */}
                    <div className="mt-auto flex gap-2">
                      <button type="button" onClick={() => handleEdit(f)}
                        className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:border-primary hover:text-primary transition-colors">
                        <FiEdit2 size={14} /> Editar
                      </button>
                      <Popconfirm
                        title="¿Eliminar familiar?"
                        description="Esta acción no se puede deshacer"
                        onConfirm={() => handleDelete(f._id)}
                        okText="Eliminar" cancelText="Cancelar"
                        okButtonProps={{ danger: true }}
                      >
                        <button type="button" aria-label={`Eliminar a ${f.nombre}`}
                          className="w-11 h-11 flex-shrink-0 inline-flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600 transition-colors">
                          <FiTrash2 size={15} />
                        </button>
                      </Popconfirm>
                    </div>
                  </div>
                </article>
              );
            })}

            {/* Tarjeta para agregar otro */}
            <button type="button" onClick={handleCreate}
              className="group min-h-[240px] flex flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-gray-200 bg-white/50 text-gray-500 hover:border-secondary hover:bg-[#fffbea] hover:text-[#00266b] transition-colors">
              <span className="w-14 h-14 rounded-full bg-blue-50 text-primary flex items-center justify-center group-hover:bg-secondary group-hover:text-[#00266b] transition-colors">
                <FiPlus size={24} />
              </span>
              <span className="font-bold text-sm">Agregar familiar</span>
            </button>
          </div>
        )}
      </div>

      {/* ── Modal crear/editar ──────────────────────────────────────────────── */}
      <Modal
        title={
          <Space>
            <UserOutlined style={{ color: '#0047bb' }} />
            <span>{modalMode === 'create' ? 'Agregar Familiar' : 'Editar Familiar'}</span>
          </Space>
        }
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={[
          <Button key="cancel" onClick={handleCloseModal}>Cancelar</Button>,
          <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()} style={{ background: '#0047bb', borderColor: '#0047bb' }}>
            {modalMode === 'create' ? 'Crear familiar' : 'Guardar cambios'}
          </Button>,
        ]}
        width={600}
        afterClose={() => { form.resetFields(); setSelectedFamiliar(null); }}
        forceRender
      >
        <FormContent
          form={form}
          mode={modalMode}
          validarEdadPermitida={validarEdadPermitida}
          onFinish={handleSubmit}
          initialValues={selectedFamiliar}
          user={user}
        />
      </Modal>
    </div>
  );
}

// ── Formulario (sin cambios en la lógica) ────────────────────────────────────
function FormContent({ form, mode, validarEdadPermitida, onFinish, initialValues, user }) {
  const [edadCalculada,  setEdadCalculada]  = React.useState(null);
  const [categoriaEdad,  setCategoriaEdad]  = React.useState(null);

  React.useEffect(() => {
    if (mode === 'edit' && initialValues?.fechaNacimiento) {
      handleFechaChange(dayjs(initialValues.fechaNacimiento));
    }
  }, [mode, initialValues]);

  const handleFechaChange = (date) => {
    if (date && date.$d) {
      try {
        const hoy  = dayjs();
        const edad = hoy.diff(dayjs(date), 'year');
        setEdadCalculada(edad);
        if (edad < 0)        setCategoriaEdad('futura');
        else if (edad < 18)  setCategoriaEdad('menor');
        else if (edad >= 60) setCategoriaEdad('mayor');
        else                 setCategoriaEdad('adulto');
      } catch { setEdadCalculada(null); setCategoriaEdad(null); }
    } else {
      setEdadCalculada(null);
      setCategoriaEdad(null);
    }
  };

  const getEdadMessage = () => {
    if (edadCalculada === null) return null;
    if (categoriaEdad === 'futura') return <span style={{ color: '#ff4d4f', fontSize: 12 }}>✗ La fecha no puede ser en el futuro</span>;
    if (categoriaEdad === 'menor')  return <span style={{ color: '#0047bb', fontSize: 12 }}>✓ Menor de edad ({edadCalculada} años)</span>;
    if (categoriaEdad === 'mayor')  return <span style={{ color: '#0047bb', fontSize: 12 }}>✓ Adulto mayor ({edadCalculada} años)</span>;
    if (categoriaEdad === 'adulto') return <span style={{ color: '#ff4d4f', fontSize: 12 }}>✗ Edad no permitida ({edadCalculada} años). Solo menores o mayores de 60.</span>;
    return null;
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} preserve={false} style={{ marginTop: 16 }}>
      <Form.Item name="nombre" label="Nombre completo"
        rules={[{ required: true, message: 'Requerido' }, { min: 3, message: 'Mínimo 3 caracteres' }]}
      >
        <Input prefix={<UserOutlined />} placeholder="Ej: Juan Pérez González" size="large" />
      </Form.Item>

      <Form.Item name="rut" label="RUT"
        rules={[{ required: true, message: 'Requerido' }, { pattern: /^[0-9]{7,8}-[0-9kK]$/, message: 'Formato: 12345678-9' }]}
        extra="Formato: 12345678-9"
      >
        <Input prefix={<ExclamationCircleOutlined />} placeholder="12345678-9" size="large" disabled={mode === 'edit'} />
      </Form.Item>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Form.Item name="fechaNacimiento" label="Fecha de nacimiento"
          validateTrigger="onBlur"
          rules={[{ required: true, message: 'Requerida' }, { validator: validarEdadPermitida }]}
          help={getEdadMessage()}
        >
          <DatePicker className="w-full" format="DD/MM/YYYY" size="large" placeholder="Selecciona fecha"
            onChange={handleFechaChange}
            disabledDate={(c) => c && c > dayjs().endOf('day')}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="genero" label="Género" rules={[{ required: true, message: 'Requerido' }]}>
          <Select placeholder="Selecciona género" size="large">
            <Option value="masculino">Masculino</Option>
            <Option value="femenino">Femenino</Option>
            <Option value="otro">Otro</Option>
          </Select>
        </Form.Item>
      </div>

      <Form.Item name="telefono" label="Teléfono (opcional)"
        rules={[{ pattern: /^(\+56)?[0-9]{9,11}$/, message: 'Formato inválido' }]}
        extra={user?.phoneNumber && <span style={{ fontSize: 12, color: '#9ca3af' }}>Si lo dejas vacío, se usará: <strong>{user.phoneNumber}</strong></span>}
      >
        <Input prefix={<PhoneOutlined />} placeholder={user?.phoneNumber || "+56 9 1234 5678"} size="large" />
      </Form.Item>

      <Form.Item name="email" label="Email (opcional)"
        rules={[{ type: 'email', message: 'Email inválido' }]}
        extra={user?.email && <span style={{ fontSize: 12, color: '#9ca3af' }}>Si lo dejas vacío, se usará: <strong>{user.email}</strong></span>}
      >
        <Input prefix={<MailOutlined />} placeholder={user?.email || "correo@ejemplo.com"} size="large" />
      </Form.Item>

      <Divider style={{ margin: '8px 0 16px' }} />

      <Form.Item
        name="tieneCondicion"
        label="¿Tiene alguna enfermedad de base o condición especial?"
        rules={[{ required: true, message: 'Indica sí o no' }]}
        extra={<span style={{ fontSize: 12, color: '#9ca3af' }}>
          Esta información permite al equipo del recinto actuar frente a una emergencia.
        </span>}
      >
        <Radio.Group buttonStyle="solid" size="large">
          <Radio.Button value="no">No</Radio.Button>
          <Radio.Button value="si">Sí</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.tieneCondicion !== cur.tieneCondicion}>
        {({ getFieldValue }) => getFieldValue('tieneCondicion') === 'si' && (
          <Form.Item
            name="enfermedadesBases"
            label="Detalle de la condición"
            rules={[
              { required: true, message: 'Describe la condición' },
              { min: 3, message: 'Mínimo 3 caracteres' },
            ]}
          >
            <Input.TextArea
              rows={3}
              maxLength={500}
              showCount
              placeholder="Ej: epilepsia, asma, alergia a penicilina, TEA, diabetes. Incluye medicamentos o cuidados relevantes."
            />
          </Form.Item>
        )}
      </Form.Item>
    </Form>
  );
}
